import {
  convertAreaType,
  convertArtRrt,
  convertMethod,
  convertModality,
  convertObjective,
  convertPropertyType,
  PreFlightDocumentType,
  PreFlightStatus,
} from '@/api/preFlight/enumerations';
import {
  IconAttachFileMS,
  IconDownloadMS,
  IconEditNoteMS,
} from '@/constants/icons';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { PreFlightData } from '../../../../api/preFlight/types';
import StandardMap from '../../../../components/Maps/StandardMap';
import { Constants } from '../../constants';
import { ApproveConfirmationDialog } from './ApproveConfirmationDialog';
import { RejectConfirmationDialog } from './RejectConfirmationDialog';
import {
  ApproveButton,
  ApproveRejectBar,
  AttachmentsBox,
  CompanionBox,
  Content,
  DocumentBox,
  EditButton,
  FileContainer,
  FileList,
  FileNameTypography,
  InputTitle,
  RegisterBox,
  RejectButton,
  SectionTitle,
  StatusTag,
  StyledIcon,
} from './styles';

interface StaticViewProps {
  selectedPreflight: PreFlightData;
  onEdit: () => void;
  isActionsDisabled: boolean;
  loadingApprove: boolean;
  loadingReject: boolean;
  onApprove: () => void;
  onReject: (reason: string) => void;
}

export function StaticView({
  selectedPreflight,
  onEdit,
  isActionsDisabled,
  loadingApprove,
  loadingReject,
  onApprove,
  onReject,
}: StaticViewProps): JSX.Element {
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);

  const {
    requester,
    inspection,
    documents,
    purpose,
    appraisal,
    address,
    registry,
    art_rrt: artRrt,
    addons,
  } = selectedPreflight;
  const matriculaDoc = documents.find(
    (d) => d.type === PreFlightDocumentType.DEED
  );

  const downloadFile = (link: string): void => {
    const downloadLink = document.createElement('a');
    downloadLink.href = link;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {selectedPreflight.status === PreFlightStatus.PENDING ? (
          <Box display="flex" gap="16px" mb="8px">
            <EditButton onClick={onEdit}>
              {IconEditNoteMS}
              {Constants.editOs}
            </EditButton>
          </Box>
        ) : (
          <Box display="flex" gap="16px" mb="8px" justifyContent="right">
            <StatusTag status={selectedPreflight.status}>
              {selectedPreflight.status === PreFlightStatus.APPROVED
                ? Constants.approvedStatus
                : Constants.rejectedStatus}
            </StatusTag>
          </Box>
        )}
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.requester}</SectionTitle>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.requesterName}</InputTitle>
        <Content>{requester?.name || '—'}</Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.phone}</InputTitle>
        <Content>{requester?.phone || '—'}</Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.email}</InputTitle>
        <Content>{requester?.email || '—'}</Content>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.purpose}</SectionTitle>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.goal}</InputTitle>
        <Content>
          {purpose.objective ? convertObjective(purpose.objective) : '—'}
        </Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.propertyType}</InputTitle>
        <Content>
          {purpose.property_type
            ? convertPropertyType(purpose.property_type)
            : '—'}
        </Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.concept}</InputTitle>
        <Content>
          {purpose.area_type ? convertAreaType(purpose.area_type) : '—'}
        </Content>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.report}</SectionTitle>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.modality}</InputTitle>
        <Content>
          {appraisal.modality ? convertModality(appraisal.modality) : '—'}
        </Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.calculationType}</InputTitle>
        <Content>
          {appraisal.method ? convertMethod(appraisal.method) : '—'}
        </Content>
      </Grid>
      <Grid item xs={12}>
        <CompanionBox>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SectionTitle>{Constants.companion}</SectionTitle>
            </Grid>
            <Grid item xs={6} xl={4}>
              <InputTitle>{Constants.requesterName}</InputTitle>
              <Content>{inspection?.name || '—'}</Content>
            </Grid>
            <Grid item xs={6} xl={4}>
              <InputTitle>{Constants.email}</InputTitle>
              <Content>{inspection?.email || '—'}</Content>
            </Grid>
            <Grid item xs={6} xl={4}>
              <InputTitle>{Constants.phone}</InputTitle>
              <Content>{inspection?.phone || '—'}</Content>
            </Grid>
          </Grid>
        </CompanionBox>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.address}</SectionTitle>
      </Grid>
      <Grid item xs={12}>
        <InputTitle>{Constants.street}</InputTitle>
        <Content>{address.street || '—'}</Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.number}</InputTitle>
        <Content>{address.number || '—'}</Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.complement}</InputTitle>
        <Content>{address.complement || '—'}</Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.zipCode}</InputTitle>
        <Content>{address.zipcode || '—'}</Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.district}</InputTitle>
        <Content>{address.neighborhood || '—'}</Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.city}</InputTitle>
        <Content>{address.city || '—'}</Content>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.state}</InputTitle>
        <Content>{address.uf || '—'}</Content>
      </Grid>
      {address.latitude && address.longitude && (
        <Grid item xs={12}>
          <Box p="8px 24px">
            <StandardMap
              pinPlace={{ lat: address.latitude, lng: address.longitude }}
              draggable={false}
            />
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.registration}</SectionTitle>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.registrationNumber}</InputTitle>
        <Content>{registry.deed_number || '—'}</Content>
      </Grid>
      <Grid item xs={12}>
        <RegisterBox>
          {matriculaDoc ? (
            <>
              <Box>
                <Typography fontSize={14} fontWeight={500}>
                  {Constants.registrationFile}
                </Typography>
                <Typography fontSize={11} color="text.secondary">
                  {Constants.fileFormats}
                </Typography>
              </Box>
              <Box display="flex" gap="8px">
                <EditButton
                  onClick={() => downloadFile(matriculaDoc.download_url)}
                >
                  {IconDownloadMS}
                  {Constants.downloadFile}
                </EditButton>
              </Box>
            </>
          ) : (
            <Box>
              <Typography fontSize={14} fontWeight={500}>
                {Constants.registrationFile}
              </Typography>
              <Typography fontSize={11} color="text.secondary">
                {Constants.noDeedFile}
              </Typography>
            </Box>
          )}
        </RegisterBox>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.artRrt}</SectionTitle>
      </Grid>
      <Grid item xs={6} xl={4}>
        <InputTitle>{Constants.artModality}</InputTitle>
        <Content>{artRrt ? convertArtRrt(artRrt) : '—'}</Content>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.complementaryServices}</SectionTitle>
      </Grid>
      <Grid item xs={4}>
        <InputTitle>{Constants.technicalReview}</InputTitle>
        <Content>
          {addons.technical_review ? Constants.yes : Constants.no}
        </Content>
      </Grid>
      <Grid item xs={4}>
        <InputTitle>{Constants.newRegistration}</InputTitle>
        <Content>{addons.updated_deed ? Constants.yes : Constants.no}</Content>
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ margin: '8px 0' }} />
      </Grid>
      <Grid item xs={12}>
        <SectionTitle>{Constants.documents}</SectionTitle>
      </Grid>
      <Grid item xs={12}>
        <DocumentBox sx={{ marginTop: '0' }}>
          <AttachmentsBox>
            {documents.length === 0 && (
              <Typography>{Constants.noAttachments}</Typography>
            )}
            <FileList>
              {documents.map((file) => (
                <FileContainer container key={file.download_url}>
                  <Box>
                    <FileNameTypography>
                      {IconAttachFileMS}
                      {file.original_filename}
                    </FileNameTypography>
                  </Box>
                  <Box>
                    <StyledIcon onClick={() => downloadFile(file.download_url)}>
                      {IconDownloadMS}
                    </StyledIcon>
                  </Box>
                </FileContainer>
              ))}
            </FileList>
          </AttachmentsBox>
        </DocumentBox>
      </Grid>
      {selectedPreflight.status === PreFlightStatus.PENDING && (
        <>
          <Grid item xs={12}>
            <ApproveRejectBar>
              <RejectButton
                onClick={() => setOpenRejectModal(true)}
                disabled={isActionsDisabled}
              >
                {loadingReject ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  Constants.rejectOs
                )}
              </RejectButton>
              <ApproveButton
                variant="contained"
                onClick={() => setOpenApproveModal(true)}
                disabled={isActionsDisabled}
              >
                {loadingApprove ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  Constants.approveOs
                )}
              </ApproveButton>
            </ApproveRejectBar>
          </Grid>
          <ApproveConfirmationDialog
            open={openApproveModal}
            onClose={() => setOpenApproveModal(false)}
            onConfirm={onApprove}
            loading={loadingApprove}
          />
          <RejectConfirmationDialog
            open={openRejectModal}
            onClose={() => setOpenRejectModal(false)}
            onConfirm={onReject}
            loading={loadingReject}
            osNumber={selectedPreflight.work_order.reference_number}
          />
        </>
      )}
    </Grid>
  );
}
